import React, { memo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { FaCheckDouble, FaBan, FaClock, FaHandPaper } from "react-icons/fa";
import { Icon, Tooltip, Text } from "@chakra-ui/react";
import DraftStatuses from "../../../_constants/DraftStatuses";

const DraftStatusIcon = ({
  draft,
  showStatusLabel,
  hideOnDraftStatus,
  ...rest
}) => {
  const { t } = useTranslation("planner");

  if (
    hideOnDraftStatus &&
    (draft?.postStatus?.id === DraftStatuses.NEW ||
      draft?.postStatus?.id === DraftStatuses.SAVED)
  ) {
    /*
      some places (f.e draft modal) already show "Draft" and only need approved/rejected information,
      so that's where this component shouldn't render anything;
    */
    return null;
  }

  if (draft?.postStatus?.id === DraftStatuses.ACCEPTED) {
    return (
      <Tooltip
        label={t("drafts.approved_by", {
          name: draft.approvedByUser?.name,
        })}
        placement="top"
        hasArrow
      >
        <Text
          d="flex"
          alignItems="center"
          fontWeight="normal"
          fontSize="xs"
          {...rest}
        >
          <Icon fontSize="xs" as={FaCheckDouble} color="brand.positive" />
          {showStatusLabel && (
            <Text as="span" ml="1">
              {t("drafts.approved")}

              <Text as="span" display={["none", "none", "inline"]}>
                {" "}
                {t("by", { ns: "common" })} {draft.approvedByUser?.name}
              </Text>
            </Text>
          )}
        </Text>
      </Tooltip>
    );
  }

  if (draft?.postStatus?.id === DraftStatuses.ACCEPTED_EXTERNAL) {
    return (
      <Tooltip
        label={t("drafts.approved_externally")}
        placement="top"
        hasArrow
      >
        <Text
          d="flex"
          alignItems="center"
          fontWeight="normal"
          fontSize="xs"
          {...rest}
        >
          <Icon fontSize="xs" as={FaCheckDouble} color="brand.positive" />
          {showStatusLabel && (
            <Text as="span" ml="1">
              {t("drafts.approved_externally")}
            </Text>
          )}
        </Text>
      </Tooltip>
    );
  }

  if (draft?.postStatus?.id === DraftStatuses.REJECTED) {
    return (
      <Tooltip
        label={t("drafts.rejected_by", {
          name: draft.rejectedByUser?.name,
        })}
        placement="top"
        hasArrow
      >
        <Text
          d="flex"
          alignItems="center"
          fontWeight="normal"
          fontSize="xs"
          {...rest}
        >
          <Icon fontSize="xs" as={FaBan} color="brand.negative" />
          {showStatusLabel && (
            <Text as="span" ml="1">
              {draft?.postStatus?.id}

              <Text as="span" display={["none", "none", "inline"]}>
                {" "}
                {t("by", { ns: "common" })} {draft?.rejectedByUser?.name}
              </Text>
            </Text>
          )}
        </Text>
      </Tooltip>
    );
  }

  if (draft?.postStatus?.id === DraftStatuses.REJECTED_EXTERNAL) {
    return (
      <Tooltip
        label={t("drafts.rejected_externally")}
        placement="top"
        hasArrow
      >
        <Text
          d="flex"
          alignItems="center"
          fontWeight="normal"
          fontSize="xs"
          {...rest}
        >
          <Icon fontSize="xs" as={FaBan} color="brand.negative" />
          {showStatusLabel && (
            <Text as="span" ml="1">
              {t("drafts.rejected_externally")}
            </Text>
          )}
        </Text>
      </Tooltip>
    );
  }

  if (draft?.postStatus?.id === DraftStatuses.DONE) {
    return (
      <Tooltip label={t("stage.awaiting_approval")} placement="top" hasArrow>
        <Text
          d="flex"
          alignItems="center"
          fontWeight="normal"
          fontSize="xs"
          {...rest}
        >
          <Icon fontSize="xs" as={FaHandPaper} />
          {showStatusLabel && (
            <Text as="span" ml="1">
              {draft?.postStatus?.id}
            </Text>
          )}
        </Text>
      </Tooltip>
    );
  }

  if (
    draft?.postStatus?.id === DraftStatuses.NEW ||
    draft?.postStatus?.id === DraftStatuses.SAVED
  ) {
    return (
      <Text
        d="flex"
        alignItems="center"
        fontWeight="normal"
        fontSize="xs"
        {...rest}
      >
        <Icon fontSize="xs" as={FaClock} />
        {showStatusLabel && (
          <Text as="span" ml="1">
            {draft?.postStatus?.id}
          </Text>
        )}
      </Text>
    );
  }

  return null;
};

DraftStatusIcon.propTypes = {
  size: PropTypes.string,
  draft: PropTypes.object,
  hideOnDraftStatus: PropTypes.bool,
  showStatusLabel: PropTypes.bool,
};

DraftStatusIcon.defaultProps = {
  size: "sm",
  status: undefined,
  hideOnDraftStatus: false,
  showStatusLabel: false,
};

export default memo(DraftStatusIcon);
